import { Link } from "@tanstack/react-router";
import {
  BookOpen,
  Gauge,
  History,
  Home,
  Stethoscope,
  type LucideIcon,
} from "lucide-react";

type NavItem = {
  label: string;
  icon: LucideIcon;
  to: string;
  search?: Record<string, unknown>;
};

export const NAV: NavItem[] = [
  {
    label: "الرئيسية",
    icon: Home,
    to: "/",
  },
  {
    label: "فحص جديد",
    icon: Stethoscope,
    to: "/diagnose",
    search: {
      node: "motor_not_running",
    },
  },
  {
    label: "مكتبة الأعطال",
    icon: BookOpen,
    to: "/library",
    search: {
      cat: undefined,
      q: "",
    },
  },
  {
    label: "سجل التشخيص",
    icon: History,
    to: "/history",
  },
  {
    label: "أدوات القياس",
    icon: Gauge,
    to: "/tools",
  },
];

export function AppShell({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen circuit-bg">

      <header className="sticky top-0 z-40 panel-dark border-b border-white/10">

        <div className="mx-auto grid w-full max-w-5xl grid-cols-[auto_minmax(0,1fr)] items-center gap-3 px-4 py-3">

          <div className="flex min-w-0 items-center gap-3">

            {/* اللوغو: يرجع إلى الصفحة الرئيسية لموقع المهندس */}
            <button
              type="button"
              aria-label="الرجوع إلى الصفحة الرئيسية لموقع المهندس"
              onClick={() => {
                const engineerNavigate = (window as Window & {
                  REACT_APP_NAVIGATE?: (to: string) => void;
                }).REACT_APP_NAVIGATE;

                if (engineerNavigate) {
                  engineerNavigate("/");
                } else {
                  window.location.assign("/");
                }
              }}
              className="press shrink-0 cursor-pointer"
            >
              <img
                src="/faults-app/header-logo.png"
                alt="شعار فاحص الأعطال الكهربائي الذكي"
                width={48}
                height={48}
                className="h-11 w-11 rounded-xl bg-white p-0.5 shadow-glow object-contain"
              />
            </button>

            <Link
              to="/"
              className="press min-w-0"
            >
              <span className="block truncate text-base font-extrabold text-white sm:text-lg">
                فاحص الأعطال الكهربائي الذكي
              </span>

              <span className="block truncate text-xs text-primary">
                شخّص العطل خطوة بخطوة
              </span>
            </Link>

          </div>

          <nav className="hidden justify-end gap-1 lg:flex">

            {NAV.map((n) => (

              <Link
                key={n.to}
                to={n.to}
                search={n.search as never}
                activeProps={{
                  className:
                    "bg-primary text-primary-foreground",
                }}
                className="press rounded-xl px-3 py-2 text-sm font-bold text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >

                <span className="flex items-center gap-2">

                  <n.icon className="h-4 w-4" />

                  {n.label}

                </span>

              </Link>

            ))}

          </nav>

        </div>

        <div className="h-1 w-full bg-linear-to-l from-primary-dark via-primary to-primary-glow" />

      </header>

      <div className="pb-6">
        {children}
      </div>

      {/* حقوق الكتابة والبرمجة */}
      <footer className="mx-auto w-full max-w-5xl px-2 pb-24 pt-4 text-center lg:pb-5">
        <p className="whitespace-nowrap text-[9px] font-bold text-muted-foreground sm:text-xs">
          تمت كتابة الاعطال و البرمجة من قبل (Murtadha abdulzahra)
        </p>
      </footer>

      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 backdrop-blur lg:hidden">

        <div className="mx-auto grid max-w-5xl grid-cols-5">

          {NAV.map((n) => (

            <Link
              key={n.to}
              to={n.to}
              search={n.search as never}
              activeProps={{
                className: "text-primary-dark",
              }}
              inactiveProps={{
                className:
                  "text-muted-foreground",
              }}
              className="press flex flex-col items-center gap-1 py-2 text-[11px] font-bold"
            >

              <n.icon className="h-5 w-5" />

              <span className="truncate px-1">
                {n.label}
              </span>

            </Link>

          ))}

        </div>

      </nav>

    </div>
  );
}

export function StatusBadge({
  tone,
  children,
}: {
  tone: "danger" | "warning" | "ok" | "info";
  children: React.ReactNode;
}) {
  const map = {
    danger:
      "bg-destructive/12 text-destructive border-destructive/30",

    warning:
      "bg-warning/15 text-warning-foreground border-warning/40",

    ok:
      "bg-success/12 text-success border-success/30",

    info:
      "bg-muted text-muted-foreground border-border",
  } as const;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${map[tone]}`}
    >
      {children}
    </span>
  );
}

export function severityToneKey(
  s?: string | null,
): "danger" | "warning" | "ok" | "info" {
  if (s === "حرجة") return "danger";

  if (s === "عالية") return "warning";

  if (s === "متوسطة") return "info";

  return "ok";
}